import React, { useCallback, useEffect, useMemo, useState } from "react";

import { convert, LocalTime, ZonedDateTime } from "@js-joda/core";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import {
  AnyFilterExpressionInput,
  MaintenanceDto,
  MaintenanceStatus,
  SortDirection,
  SortInput,
  useGetMaintenanceByProjectIdLazyQuery,
  useGetSimpleObjectsByProjectQuery,
} from "gql/generated/types";
import { useTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { fmtDatetime } from "utils/time-utils";
import LazyTable from "../components/LazyTable";
import MaintenanceDialogButton from "../components/Maintenance/MaintenanceDialogButton";
import MaintenanceFormDialog from "../components/Maintenance/MaintenanceFormDialog";
import MaintenanceMap from "../components/Maintenance/MaintenanceMap";
import MaintenanceSummary from "../components/Maintenance/MaintenanceSummary";
import TableColumnSelection from "../components/TableColumnSelection";
import useCustomColumns from "../hooks/useCustomColumns";
import useProjectContext from "../hooks/useProjectContext";
import useTableFilter from "../hooks/useTableFilter";
import { initialFilter, maintenanceStatuses } from "../utils/const";
import { TableColumn } from "../utils/types";
import { apolloErrorToaster, renderTag, searchMaintenanceFilter } from "../utils/utils";

const SORT_FIELD = "submissionTime";

const Maintenance: React.FC = () => {
  const tz = useTimezone();
  const [objectListDialog, setObjectListDialog] = useState(false);
  const [formDialog, setFormDialog] = useState(false);
  const [newMaintenance, setNewMaintenance] = useState<MaintenanceDto>();
  const [objectIds, setObjectIds] = useState<string[]>([]);
  const { projectId, project, userSettings } = useProjectContext();
  const [dateFrom, setDateFrom] = useState<Date>(
    convert(ZonedDateTime.now(tz.company).minusYears(1)).toDate()
  );
  const [dateTo, setDateTo] = useState<Date>(
    convert(ZonedDateTime.now(tz.company).with(LocalTime.MAX)).toDate()
  );
  const [tableOptions, setTableOptions] = useTableFilter("Maintenance", {
    sortField: SORT_FIELD,
    reqSortField: SORT_FIELD,
    sortOrder: -1,
  });

  const [getMaintenance, { data, loading, previousData }] = useGetMaintenanceByProjectIdLazyQuery({
    onError: apolloErrorToaster,
  });

  const [getIsoMaintenance] = useGetMaintenanceByProjectIdLazyQuery();

  const total =
    data?.allMaintenanceByProjectIdAndDateRangePaged.pageInfo?.total ??
    previousData?.allMaintenanceByProjectIdAndDateRangePaged.pageInfo?.total ??
    0;

  const maintenanceData = (data?.allMaintenanceByProjectIdAndDateRangePaged.items ??
    previousData?.allMaintenanceByProjectIdAndDateRangePaged.items ??
    []) as MaintenanceDto[];

  const renderService = useCallback(
    (maintenance: MaintenanceDto) => {
      const services = project?.mdmProjectById.configuration?.maintenanceConfiguration.interruptedServices;
      return services?.find(is => is?.id === maintenance.interruptedServices)?.title ?? "-";
    },
    [project?.mdmProjectById.configuration?.maintenanceConfiguration.interruptedServices]
  );

  const renderObjects = (maintenance: MaintenanceDto) => {
    return (
      <Button
        label={maintenance.objectIds?.length.toString()}
        icon="pi pi-building"
        text
        iconPos="right"
        size="small"
        className="w-min"
        onClick={() => {
          setObjectListDialog(true);
          setObjectIds((maintenance.objectIds as string[]) ?? []);
        }}
      />
    );
  };

  const renderActions = useCallback(
    (rowData: MaintenanceDto) => {
      const handleDuplicate = () => {
        setFormDialog(true);
        setNewMaintenance({
          title: rowData.title,
          reason: rowData?.reason,
          comment: rowData?.comment,
          interruptedServices: rowData?.interruptedServices,
          objectIds: rowData?.objectIds,
          submissionTime: new Date(),
          status: MaintenanceStatus.Registered,
          projectId,
        });
      };

      return (
        <div className="flex gap-2">
          <Link to={`${rowData.id}`}>
            <Button
              size="small"
              type="button"
              icon="pi pi-pencil"
              text
              className="p-1"
              style={{ width: "1.3rem" }}
            />
          </Link>
          <Button
            type="button"
            title={T("action.duplicate")}
            icon="pi pi-clone"
            severity="warning"
            className="p-1"
            style={{ width: "1.3rem" }}
            text
            onClick={handleDuplicate}
          />
        </div>
      );
    },
    [projectId]
  );

  const columns = useMemo(
    (): TableColumn<MaintenanceDto>[] => [
      {
        field: "title",
        header: T("word.title"),
        sortable: true,
        columnKey: "title",
      },
      {
        field: "status",
        header: T("word.status"),
        sortable: true,
        body: renderTag,
        columnKey: "status",
      },
      {
        field: "submissionTime",
        header: T("word.submission-time"),
        sortable: true,
        body: rowData => rowData.submissionTime && fmtDatetime(rowData.submissionTime, tz.display),
        columnKey: "submissionTime",
      },
      {
        field: "approvalTime",
        header: T("word.approval-time"),
        sortable: true,
        body: rowData => rowData.approvalTime && fmtDatetime(rowData.approvalTime, tz.display),
        columnKey: "approvalTime",
      },
      {
        field: "disconnectionTime",
        header: T("word.disconnection-time"),
        sortable: true,
        body: rowData => rowData.disconnectionTime && fmtDatetime(rowData.disconnectionTime, tz.display),
        columnKey: "disconnectionTime",
      },
      {
        field: "reconnectionTime",
        header: T("word.reconnection-time"),
        sortable: true,
        body: rowData => rowData.reconnectionTime && fmtDatetime(rowData.reconnectionTime, tz.display),
        columnKey: "reconnectionTime",
      },
      {
        field: "executionTime",
        header: T("word.execution-time"),
        sortable: true,
        body: rowData => rowData.executionTime && fmtDatetime(rowData.executionTime, tz.display),
        columnKey: "executionTime",
      },
      {
        field: "interruptedServices",
        header: T("word.interrupted-services"),
        body: renderService,
        sortable: true,
        columnKey: "interruptedServices",
      },
      {
        field: "reason",
        header: T("word.reason"),
        sortable: true,
        columnKey: "reason",
      },
      {
        field: "comment",
        header: T("word.comment"),
        sortable: true,
        columnKey: "comment",
      },
      {
        field: "objectIds",
        header: T("mdm.objects"),
        body: renderObjects,
        columnKey: "objectIds",
        hideInFile: true,
      },
      {
        header: T("word.actions"),
        body: renderActions,
        columnKey: "actions",
        hideInFile: true,
      },
    ],
    [renderActions, renderService]
  );

  const [
    customColumns,
    setCustomColumns,
    formattedColumns,
    mapCols,
    formatObjectFields,
    handleColumnSave,
    updateLoading,
  ] = useCustomColumns("Maintenance", columns, maintenanceData, tableOptions.rows);

  const fetchData = useCallback(
    async (
      take: number | null = null,
      skip: number | null = null,
      sort: SortInput[] | null = null,
      filters: AnyFilterExpressionInput[] | null = null,
      cacheAndNetwork: boolean = false,
      filter: AnyFilterExpressionInput | null = null,
      isolatedFetch: boolean = false
    ) => {
      const fetchFn = isolatedFetch ? getIsoMaintenance : getMaintenance;
      const resp = await fetchFn({
        variables: {
          input: {
            id: projectId!,
            range: {
              start: dateFrom,
              startInclusive: true,
              end: dateTo,
              endInclusive: true,
            },
            take: take ?? tableOptions.rows,
            skip: skip ?? tableOptions.first,
            sort: sort ?? [
              {
                property: tableOptions.reqSortField,
                direction: tableOptions.sortOrder === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: filters ?? tableOptions.andFilters,
            filter: filter ?? tableOptions.filter,
          },
        },
        fetchPolicy: cacheAndNetwork ? "cache-and-network" : "cache-first",
      });
      return resp.data;
    },
    [
      dateFrom,
      dateTo,
      getIsoMaintenance,
      getMaintenance,
      projectId,
      tableOptions.andFilters,
      tableOptions.filter,
      tableOptions.first,
      tableOptions.reqSortField,
      tableOptions.rows,
      tableOptions.sortOrder,
    ]
  );

  useEffect(() => {
    if (userSettings) {
      fetchData(tableOptions.rows);
    }
  }, [tableOptions, userSettings, fetchData]);

  const resetFilters = () => {
    setTableOptions(prev => ({
      ...initialFilter,
      rows: prev.rows,
      sortField: SORT_FIELD,
      reqSortField: SORT_FIELD,
      sortOrder: -1,
    }));
  };

  const handleSearch = (value: string) => {
    setTableOptions(prev => ({
      ...prev,
      filter: value ? searchMaintenanceFilter(value) : null,
      first: 0,
      search: value,
    }));
  };

  const extraTools = (
    <>
      <TableColumnSelection
        customColumns={customColumns}
        tableName="Maintenance"
        setCustomColumns={setCustomColumns}
        handleColumnSave={handleColumnSave}
        mapCol={mapCols}
        loading={updateLoading}
      />
      <MaintenanceDialogButton />
    </>
  );

  const centerTools = (
    <div className="flex gap-3" style={{ height: "35.4px" }}>
      <div className="flex align-items-center gap-2">
        <strong>{T("word.from")}</strong>
        <CalendarWrapper value={dateFrom} onChange={setDateFrom} showTime={false} className="w-10rem" />
      </div>
      <div className="flex align-items-center gap-2">
        <strong>{T("word.to")}</strong>
        <CalendarWrapper
          value={dateTo}
          className="w-10rem"
          onChange={date => setDateTo(new Date(date.setHours(23, 59, 59, 999)))}
          showTime={false}
        />
      </div>
    </div>
  );

  const formatExport = (exportData: MaintenanceDto, field: string) => {
    if (field === "interruptedServices") {
      return (
        project?.mdmProjectById.configuration?.maintenanceConfiguration.interruptedServices?.find(
          is => is?.id === exportData.interruptedServices
        )?.title ?? "-"
      );
    }
    if (field === "status") {
      return T(maintenanceStatuses.find(st => st.value === exportData.status)?.translationKey!);
    }
    return null;
  };

  return (
    <TabView>
      <TabPanel header={T("word.main")}>
        <LazyTable
          columns={formattedColumns}
          data={formatObjectFields}
          tableName="Maintenance"
          buttons={extraTools}
          centerTools={centerTools}
          containerName="MdmMaintenance"
          loading={loading}
          setTableOptions={setTableOptions}
          resetFilters={resetFilters}
          tableOptions={tableOptions}
          totalItems={total}
          fetchData={fetchData}
          onSearch={handleSearch}
          setCustomColumns={setCustomColumns}
          formatExportData={formatExport}
        />
        <Dialog
          header={T("mdm.objects")}
          visible={objectListDialog}
          onHide={() => setObjectListDialog(false)}
        >
          <ObjectList objectIds={objectIds} />
        </Dialog>
        <MaintenanceFormDialog
          showDialog={formDialog}
          setShowDialog={setFormDialog}
          maintenance={newMaintenance}
        />
      </TabPanel>
      <TabPanel header={T("word.summary")}>
        <MaintenanceSummary data={maintenanceData} />
      </TabPanel>
      <TabPanel header={T("word.map")}>
        <MaintenanceMap data={maintenanceData} />
      </TabPanel>
    </TabView>
  );
};

export default Maintenance;

const ObjectList: React.FC<{ objectIds: string[] }> = ({ objectIds }) => {
  const { projectId } = useProjectContext();
  const { data, loading } = useGetSimpleObjectsByProjectQuery({
    variables: {
      id: projectId!,
    },
    skip: !projectId,
    onError: apolloErrorToaster,
  });

  if (loading) return <ProgressSpinner className="m-5" />;

  const objects =
    data?.mdmObjectsByProjectId
      .filter(o => objectIds.includes(o.id!))
      .sort((a, b) => a.fullAddress!.localeCompare(b.fullAddress!)) ?? [];

  return (
    <div className="flex flex-column gap-3 m-3" style={{ maxHeight: "500px" }}>
      {objects.map(o => (
        <div key={o.id} className="flex flex-column">
          <Link to={`../objects/${o.id}`} className="hover:underline">
            {o.identifier}:
          </Link>
          <span>{o.fullAddress}</span>
        </div>
      ))}
      <span className="py-2" />
    </div>
  );
};
